import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useEffect, useMemo, useRef, useState } from "react";

const subjectMapping = {
  PAI: "Pendidikan Agama dan Budi Pekerti",
  PKN: "Pendidikan Pancasila",
  BIndo: "Bahasa Indonesia",
  MTK: "Matematika",
  IPAS: "Ilmu Pengetahuan Alam dan Sosial",
  PJOK: "Pendidikan Jasmani Olahraga dan Kesehatan",
  SENIBUDAYA: "Seni dan Budaya",
  BIng: "Bahasa Inggris",
  MULOK: "Muatan Lokal",
  Koding: "Koding dan Kecerdasan Artifisial",
};

const JurnalGuru = () => {
  const [guruList, setGuruList] = useState([]);
  const [selectedGuruId, setSelectedGuruId] = useState("");
  const [user, setUser] = useState(null);
  const [dataTP, setDataTP] = useState([]);
  const [sekolah, setSekolah] = useState([]);
  const [mapel, setMapel] = useState("");
  const [semester, setSemester] = useState("");
  const [kelas, setKelas] = useState("");
  const [rombel, setRombel] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const pdfRef = useRef();

  /* ================= FETCH SEKOLAH ================= */
  useEffect(() => {
    const fetchSekolah = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/datasekolah/all`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSekolah(res.data || []);
      } catch {
        setError("Gagal mengambil data sekolah");
      }
    };
    fetchSekolah();
  }, []);

  /* ================= FETCH GURU ================= */
  useEffect(() => {
    const fetchGuru = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/auth/user-images-and-roles`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const guruOnly = res.data.filter(
          (u) =>
            u.role?.toLowerCase() === "guru" &&
            u.jabatan?.toLowerCase() !== "kepala sekolah",
        );

        setGuruList(guruOnly);
      } catch {
        setError("Gagal mengambil data guru");
      } finally {
        setLoading(false);
      }
    };

    fetchGuru();
  }, []);

  /* ================= FETCH DETAIL GURU ================= */
  useEffect(() => {
    if (!selectedGuruId) return;
    const token = localStorage.getItem("token");

    axios
      .get(`${process.env.REACT_APP_API_URL}api/auth/users/${selectedGuruId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUser(res.data))
      .catch(() => setError("Gagal mengambil detail guru"));
  }, [selectedGuruId]);

  /* ================= AUTO SET FILTER ================= */
  useEffect(() => {
    if (!user) return;

    if (user.jabatan?.toLowerCase() === "guru mapel") {
      setMapel(user.mapel || "");
    }

    if (user.jabatan?.toLowerCase() === "guru kelas") {
      setKelas(user.kelas || "");
      setRombel(user.rombel || "");
    }
  }, [user]);

  /* ================= FETCH JURNAL ================= */
  useEffect(() => {
    const fetchTP = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}api/tp`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDataTP(res.data || []);
      } catch {
        setError("Gagal mengambil data jurnal");
      }
    };
    fetchTP();
  }, []);

  /* ================= OPTIONS ================= */
  const opsiMapel = [...new Set(dataTP.map((d) => d.mapel))];
  const opsiSemester = [...new Set(dataTP.map((d) => d.semester))];
  const opsiKelas = [...new Set(dataTP.map((d) => d.kelas))];
  const opsiRombel = [...new Set(dataTP.map((d) => d.rombel))];

  /* ================= GROUP DATA ================= */
  const jurnalGrouped = useMemo(() => {
    if (!isSearch) return {};

    const grouped = {};

    dataTP.forEach((tp) => {
      if (
        (mapel && tp.mapel !== mapel) ||
        (semester && tp.semester !== semester) ||
        (kelas && tp.kelas !== kelas) ||
        (rombel && tp.rombel !== rombel)
      )
        return;

      tp.TP?.forEach((itemTP) => {
        if (!grouped[itemTP.isiTP]) grouped[itemTP.isiTP] = [];
        itemTP.prosesTP?.forEach((p) => grouped[itemTP.isiTP].push(p));
      });
    });

    return grouped;
  }, [dataTP, mapel, semester, kelas, rombel, isSearch]);

  /* ================= DOWNLOAD PDF (PRO VERSION) ================= */
  const handleDownloadPDF = async () => {
    const element = pdfRef.current;
    if (!element) return alert("Data belum siap");

    const canvas = await html2canvas(element, {
      scale: 3, // HD
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");

    const pageWidth = 297;
    const pageHeight = 210;

    const imgWidth = 277;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 10;

    pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`Laporan_Guru_${user?.name}.pdf`);
  };

  const handleCari = () => {
    if (!selectedGuruId) return alert("Pilih guru terlebih dahulu");
    if (!semester) return alert("Pilih semester");
    setIsSearch(true);
  };

  if (loading) return <p className="text-center py-20">Loading...</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-center mb-6">
        Jurnal Kegiatan Guru
      </h1>

      <select
        value={selectedGuruId}
        onChange={(e) => setSelectedGuruId(e.target.value)}
        className="border p-2 mb-4 w-full md:w-1/3"
      >
        <option value="">Pilih Guru</option>
        {guruList.map((g) => (
          <option key={g._id} value={g._id}>
            {g.name}
          </option>
        ))}
      </select>

      {selectedGuruId && (
        <>
          <div className="grid md:grid-cols-5 gap-4 mb-4">
            <select
              value={mapel}
              onChange={(e) => setMapel(e.target.value)}
              disabled={user?.jabatan?.toLowerCase() === "guru mata pelajaran"}
              className="border p-2"
            >
              <option value="">Mapel</option>
              {opsiMapel.map((m, i) => (
                <option key={i} value={m}>
                  {subjectMapping[m] || m}
                </option>
              ))}
            </select>

            <select
              value={semester}
              onChange={(e) => setSemester(e.target.value)}
              className="border p-2"
            >
              <option value="">Semester</option>
              {opsiSemester.map((s, i) => (
                <option key={i}>{s}</option>
              ))}
            </select>

            <select
              value={kelas}
              onChange={(e) => setKelas(e.target.value)}
              disabled={user?.jabatan?.toLowerCase() === "guru kelas"}
              className="border p-2"
            >
              <option value="">Kelas</option>
              {opsiKelas.map((k, i) => (
                <option key={i}>{k}</option>
              ))}
            </select>

            <select
              value={rombel}
              onChange={(e) => setRombel(e.target.value)}
              disabled={user?.jabatan?.toLowerCase() === "guru kelas"}
              className="border p-2"
            >
              <option value="">Rombel</option>
              {opsiRombel.map((r, i) => (
                <option key={i}>{r}</option>
              ))}
            </select>

            <button
              onClick={handleCari}
              className="bg-blue-600 text-white rounded"
            >
              Cari
            </button>
          </div>

          {isSearch && (
            <>
              <button
                onClick={handleDownloadPDF}
                className="bg-green-600 text-white px-4 py-2 rounded mb-4"
              >
                Download PDF
              </button>

              <div ref={pdfRef} className="bg-white p-6 mt-10">
                <h2 className="text-xl font-bold text-center uppercase mb-2">
                  LAPORAN GURU {sekolah?.[0]?.namasekolah}
                </h2>

                <p className="text-center mb-6 font-bold uppercase">
                  Guru: {user?.name}
                </p>

                <table className="w-full border border-black text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="border border-black p-2 w-[40px]">No</th>
                      <th className="border border-black p-2 w-[200px]">
                        Kompetensi Dasar/TP
                      </th>
                      <th className="border border-black p-2">
                        Sub Kompetensi Dasar/TP
                      </th>
                      <th className="border border-black p-2 w-[100px]">
                        Tanggal
                      </th>
                      <th className="border border-black p-2">Materi</th>
                      <th className="border border-black p-2">Kecapaian</th>
                      <th className="border border-black p-2">Permasalahan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(jurnalGrouped).map(([isiTP, list], i) =>
                      list.map((p, idx) => (
                        <tr key={idx}>
                          {idx === 0 && (
                            <>
                              <td
                                rowSpan={list.length}
                                className="border border-black p-2 text-center align-top"
                              >
                                {i + 1}
                              </td>
                              <td
                                rowSpan={list.length}
                                className="border border-black p-2 align-top"
                              >
                                {isiTP}
                              </td>
                            </>
                          )}
                          <td className="border border-black p-2">
                            {p.subTP || "-"}
                          </td>
                          <td className="border border-black p-2">
                            {p.tanggal
                              ? new Date(p.tanggal).toLocaleDateString("id-ID")
                              : "-"}
                          </td>
                          <td className="border border-black p-2">
                            {p.materi || "-"}
                          </td>
                          <td className="border border-black p-2">
                            {p.kecapaian || "-"}
                          </td>
                          <td className="border border-black p-2">
                            {p.permasalahan || "-"}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default JurnalGuru;
